import CountUp from "react-countup";
import { cn } from "@/app/utils/cn";
import { useAuth } from "@/app/hooks/useAuth";
import { ReportTransactionParams } from "@/app/services/reportService/reportTransactions";
import { CurrentUserResponse } from "@/app/services/usersService/user";
import { Skeleton } from "@/view/components/ui/skeleton";
import { ItemsLabel, toMoney, useStatisticsCard } from "./useStatisticsCard";
import { PartnerType } from "@/app/utils/partners.enum";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/view/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { getProductsConfig } from "@/app/services/configService/getProductsConfig";
import { useQuery } from "@tanstack/react-query";
import { DateRangePicker } from "@/view/components/DateRangePicker";
import WithdrawModal from "../TransactionTable/WithdrawModal";
import { configService } from "@/app/services/configService";
import masks from "@/app/utils/masks";

interface StatisticsCardProps {
  interval?: number | false;
  onSelectDates?: (dates: { startDate: string; endDate: string }) => void;
  variant?: "short" | "full";
  sellerId?: string | undefined;
  reportTransactionParams?: ReportTransactionParams | undefined;
  user?: CurrentUserResponse | undefined;
  hideCommission?: boolean;
  showCalendar?: boolean;
}

interface ItemProps {
  title?: string;
  description?: string;
  start?: number;
  end?: number;
  size?: "md" | "lg" | "xl";
  variant?: "default" | "principal";
  showCurrency?: boolean;
}

const Item = ({
  title,
  description,
  start = 0,
  end = 0,
  size = "md",
  variant = "default",
  showCurrency = true,
}: ItemProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-start gap-4 xs:flex-row xs:items-start",
        variant === "principal" && "justify-between",
      )}
    >
      <div className="flex flex-col gap-1.5">
        {title && (
          <p
            className={cn(
              "text-sm font-medium text-black04",
              variant === "principal" && "text-black02",
            )}
          >
            {title}
          </p>
        )}
        <span
          className={cn(
            "flex items-baseline text-2xl font-semibold",
            size === "lg" && "text-lg",
            size === "xl" && "text-5xl",
          )}
        >
          {showCurrency && (
            <span className={cn("mr-1 text-sm", size === "lg" && "text-sm")}>
              R$
            </span>
          )}
          <CountUp
            start={start}
            end={end}
            duration={2}
            decimals={showCurrency ? 2 : 0}
            decimal={showCurrency ? "," : ""}
            separator={showCurrency ? "." : ""}
            preserveValue={true}
          />
        </span>
      </div>
      <p className="text-sm font-semibold text-black04">{description}</p>
    </div>
  );
};

interface ItemSalvCarProps {
  label: string;
  count: number;
}

const ItemSalvCar = ({ label, count }: ItemSalvCarProps) => {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm font-medium text-black04">{label}</p>
      <p className="text-2xl font-semibold ">{count}</p>
    </div>
  );
};

export function StatisticsCard({
  variant,
  hideCommission,
  showCalendar = true,
  ...props
}: StatisticsCardProps) {
  const {
    isFetching,
    isLoading,
    data,
    selectedDate,
    handleDateChange,
    availableisLoading,
    teamsContractNumber,
    amounts,
    items,
    handleChangeProduct,
    product,
  } = useStatisticsCard(props);
  const { data: options } = useQuery({
    queryKey: ["productsConfig"],
    queryFn: getProductsConfig,
    select(data) {
      if (!data) return [];
      return Object.entries(data)
        .filter(([, value]) => value === true)
        .map(([key]) => key);
    },
  });

  const { data: configs } = useQuery({
    queryKey: ["configs"],
    queryFn: () => configService.getConfigsApp(),
  });

  const { isSuperAdmin, isAdmin, isSupervisor } = useAuth();
  const { getProductLabel } = useProductsCatalog();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data) return null;

  return (
    <div className="flex grow flex-col gap-3">
      <div
        className="flex cursor-pointer items-center justify-between md:cursor-default"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <div className="flex items-center gap-1">
                <h2 className="font-semibold ">{getProductLabel(product)}</h2>
                <ChevronDown className="text-primary" />
              </div>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="center">
              {options?.map((option) => (
                <DropdownMenuItem
                  key={option}
                  onSelect={() => handleChangeProduct(option as PartnerType)}
                  className={`cursor-pointer px-4 py-4 ${
                    product === option ? "bg-gray-100 text-primary" : ""
                  }`}
                >
                  {getProductLabel(option as PartnerType)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {!isExpanded && (
          <div className="text-2xl font-bold text-black02 md:hidden">
            <span className="font-se -mr-2 text-xs">R$</span>{" "}
            {masks.currency(amounts.completed).slice(2)}
          </div>
        )}

        {showCalendar && (
          <div
            className={cn(
              "flex items-center justify-between",
              !isExpanded && "hidden md:flex",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <DateRangePicker
              date={selectedDate}
              onDateChange={handleDateChange}
              numberOfMonths={1}
            />
          </div>
        )}
      </div>

      <div
        className={cn("flex-col gap-3", isExpanded ? "flex" : "hidden md:flex")}
      >
        {isFetching || isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Skeleton className="h-[280px] w-full" />
          </div>
        ) : (
          <>
            {product === "SALVCAR" ? (
              <div className="flex flex-col gap-2">
                <Item
                  title="Pagos:"
                  end={amounts.completed}
                  variant="principal"
                  size="xl"
                  showCurrency={false}
                />
                {isSuperAdmin && !hideCommission && (
                  <div className="flex flex-col items-baseline gap-4 xs:flex-row xs:items-end">
                    {availableisLoading ? (
                      <Skeleton className="h-[36px] w-[180px]" />
                    ) : (
                      <Item
                        description={ItemsLabel.commission}
                        end={toMoney(amounts.commission)}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                  <Item
                    title="Pagos:"
                    end={toMoney(amounts.completed)}
                    description={`${(data.statistics?.completed || 0).toLocaleString("pt-BR")} Contratos`}
                    variant="principal"
                    size="xl"
                  />
                </div>
                {isSupervisor && (
                  <>
                    <Item
                      description={`Minha equipe (${data.teamStatisticas?.completed || 0})`}
                      start={0}
                      end={toMoney(amounts.myTeam)}
                    />

                    <Item
                      description={`Equipes (${(teamsContractNumber || 0).toLocaleString("pt-BR")})`}
                      start={0}
                      end={toMoney(amounts.teams)}
                    />
                  </>
                )}

                {isSuperAdmin && !hideCommission && (
                  <div className="flex flex-col items-baseline gap-4 xs:flex-row xs:items-end">
                    {availableisLoading ? (
                      <Skeleton className="h-[36px] w-[180px]" />
                    ) : (
                      <Item
                        description={ItemsLabel.commission}
                        end={toMoney(amounts.commission)}
                      />
                    )}
                  </div>
                )}

                {isAdmin && configs?.ADMIN_WITHDRAW && (
                  <div className="flex flex-col items-baseline gap-1 xs:flex-row xs:items-start xs:gap-4">
                    {availableisLoading ? (
                      <Skeleton className="h-[36px] w-[180px]" />
                    ) : (
                      <Item
                        description={
                          isSuperAdmin
                            ? ItemsLabel.partnerCommission
                            : undefined
                        }
                        end={toMoney(amounts.admin)}
                      />
                    )}
                    {!isSuperAdmin && configs?.ADMIN_WITHDRAW && (
                      <p className="text-sm font-semibold">
                        <WithdrawModal />
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div
              className={cn(
                "border-borde border border-solid xl:my-1",
                variant === "full" && "xl:mb-0",
              )}
            />
            <div
              className={cn(
                "grid grow grid-cols-1 items-end justify-items-start gap-x-4 gap-y-2",
                "xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-2",
                variant === "full" && "xl:grid-cols-4",
              )}
            >
              {product === "SALVCAR"
                ? items
                    .filter(
                      ({ label }) =>
                        label !== "Pendentes" && label !== "Estornos",
                    )
                    .map(({ count, label }) => (
                      <ItemSalvCar key={label} label={label} count={count} />
                    ))
                : items.map(({ count, label, value }) => (
                    <Item
                      key={`${label}-${value}`}
                      title={`${label} (${count})`}
                      end={toMoney(value)}
                      size="lg"
                    />
                  ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
